import { useBlocker } from '@tanstack/react-router';
import { useEffect } from 'react';

export const useConfirmLeavePage = (
  shouldPreventLeaving: boolean,
  message: string,
) => {
  const blocker = useBlocker({
    shouldBlockFn: ({ current, next }) =>
      shouldPreventLeaving && current.pathname !== next.pathname,
    withResolver: true,
  });

  useEffect(() => {
    /**
     * Handle a user trying to leave the page by requesting a different document, i.e.
     * - entering a URL into the address bar,
     * - clicking a bookmark, or
     * - reloading the page.
     */
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (shouldPreventLeaving) {
        event.preventDefault();
        event.returnValue = message;
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [shouldPreventLeaving, message]);

  useEffect(() => {
    /**
     * Handle a user trying to leave the page by using a router function, i.e.
     * - clicking on a link or
     * - clicking the back button.
     */
    if (blocker.status === 'blocked') {
      const confirmed = globalThis.confirm(message);
      if (confirmed) {
        blocker.proceed();
      } else {
        blocker.reset();
      }
    }
  }, [blocker, message]);

  useEffect(() => {
    // Reset the blocker if the user cleans the form
    if (blocker.status === 'blocked' && !shouldPreventLeaving) {
      blocker.reset();
    }
  }, [blocker, shouldPreventLeaving]);

  return blocker;
};
